import type { FeatureModule, FeatureState } from '../types';

/**
 * Lese-Hervorhebung: hebt den Text-Block unter dem Mauszeiger hervor (Absatz,
 * Überschrift, Listenpunkt). Lesehilfe v. a. bei Konzentrationsschwäche, ergänzt
 * die vorhandene Lesemaske (horizontales Band) um eine block-genaue Markierung.
 */
export default function createCursorHighlightModule(): FeatureModule {
  let enabled = false;
  let current: HTMLElement | null = null;
  const STYLE_ID = 'accessify-cursor-highlight-style';
  const CLASS = 'accessify-cursor-highlight';
  const STORAGE_KEY = 'accessify-cursor-highlight';

  function isOwnUi(el: HTMLElement | null): boolean {
    const root =
      document.getElementById('accessify-root') ||
      document.querySelector('[data-accessify-root="true"]');
    return !!el && !!root && (root === el || root.contains(el as Node));
  }

  function isTextBlock(el: HTMLElement): boolean {
    if (!el || el === document.body || el === document.documentElement) return false;
    const txt = (el.innerText || '').trim();
    if (!txt) return false;
    // Kompakte Blöcke bevorzugen (Absatz/Überschrift/li), keine Riesencontainer.
    return txt.length <= 400;
  }

  function pick(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el;
    let depth = 0;
    while (node && node !== document.body && depth < 6) {
      if (isTextBlock(node)) return node;
      node = node.parentElement;
      depth++;
    }
    return null;
  }

  function clearCurrent() {
    if (current) {
      current.classList.remove(CLASS);
      current = null;
    }
  }

  function onMove(e: MouseEvent) {
    if (!enabled) return;
    const target = e.target as HTMLElement;
    if (isOwnUi(target)) {
      clearCurrent();
      return;
    }
    const el = pick(target);
    if (el === current) return;
    clearCurrent();
    if (el) {
      el.classList.add(CLASS);
      current = el;
    }
  }

  function injectStyles() {
    let s = document.getElementById(STYLE_ID);
    if (!s) {
      s = document.createElement('style');
      s.id = STYLE_ID;
      document.head.appendChild(s);
    }
    s.textContent =
      `.${CLASS}{background-color:rgba(255,221,87,0.45)!important;` +
      `box-shadow:0 0 0 2px rgba(245,190,0,.65)!important;` +
      `border-radius:3px!important;}`;
  }

  function activate() {
    enabled = true;
    injectStyles();
    document.addEventListener('mousemove', onMove, true);
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function deactivate() {
    enabled = false;
    clearCurrent();
    document.removeEventListener('mousemove', onMove, true);
    document.getElementById(STYLE_ID)?.remove();
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'cursor-highlight',
    name: () => 'Highlight on hover',
    description: 'Highlight the text block under your mouse pointer as a reading aid',
    icon: 'cursor-highlight',
    category: 'cognitive',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'cursor-highlight', enabled }),
    setState: (state: { enabled: boolean }) => {
      if (state.enabled) activate();
      else deactivate();
    },
  };
}
